import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { CONTACT } from "../lib/constants";

const SITE_EMAIL = "info@clickphotography.gr";
const EFFECTIVE_DATE = "December 2025";

const Section = ({ n, title, children }) => (
  <section className="mt-14">
    <h2 className="font-serif text-2xl md:text-3xl text-accent-dark font-light leading-tight">
      <span className="text-gold mr-3">{n}.</span>
      {title}
    </h2>
    <span className="hairline mt-5 mb-6" />
    <div className="space-y-4 text-text-muted text-base leading-relaxed">
      {children}
    </div>
  </section>
);

const DocTitle = ({ overline, title, italic }) => (
  <div className="mt-24 first:mt-0 pt-8 border-t border-border-soft first:border-t-0 first:pt-0">
    <p className="overline">{overline}</p>
    <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight font-light text-accent-dark mt-4 leading-[1.05]">
      {title}
      <br />
      <span className="italic text-gold">{italic}</span>
    </h1>
  </div>
);

const InfoCard = () => (
  <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-border-soft bg-white p-6 text-sm">
    <div>
      <p className="text-xs tracking-[0.2em] uppercase text-gold">Website</p>
      <p className="mt-1 text-accent-dark">clickphotography.gr</p>
    </div>
    <div>
      <p className="text-xs tracking-[0.2em] uppercase text-gold">Business</p>
      <p className="mt-1 text-accent-dark">Click Photography</p>
    </div>
    <div>
      <p className="text-xs tracking-[0.2em] uppercase text-gold">Effective date</p>
      <p className="mt-1 text-accent-dark">{EFFECTIVE_DATE}</p>
    </div>
    <div>
      <p className="text-xs tracking-[0.2em] uppercase text-gold">Contact</p>
      <a
        href={`mailto:${SITE_EMAIL}`}
        className="mt-1 text-accent-dark hover:text-gold transition-colors block"
      >
        {SITE_EMAIL}
      </a>
    </div>
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-2 list-none">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="text-gold mt-2 w-1 h-1 rounded-full bg-gold shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const ContactBlock = () => (
  <div className="bg-bg-alt p-5 mt-2">
    <p className="font-medium text-accent-dark">Click Photography</p>
    <p>Website: clickphotography.gr</p>
    <p>
      Email:{" "}
      <a href={`mailto:${SITE_EMAIL}`} className="text-gold hover:underline">
        {SITE_EMAIL}
      </a>
    </p>
  </div>
);

const TocLink = ({ href, label }) => (
  <a
    href={href}
    className="block py-2 text-sm text-text-muted hover:text-gold transition-colors border-b border-border-soft last:border-0"
  >
    {label}
  </a>
);

export default function PrivacyPolicy() {
  const { lang, setLang } = useLanguage();

  useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        // Slight delay to allow layout to settle
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="bg-bg-base text-accent-dark min-h-screen font-sans">
      {/* Slim header */}
      <header className="border-b border-border-soft bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-brand"
            className="font-serif text-xl md:text-2xl tracking-tight text-accent-dark"
          >
            Click <span className="italic text-gold">Photography</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center text-[11px] tracking-[0.2em] font-medium border border-border-soft">
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 ${
                  lang === "en" ? "bg-gold text-white" : "text-accent-dark"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("gr")}
                className={`px-3 py-1.5 ${
                  lang === "gr" ? "bg-gold text-white" : "text-accent-dark"
                }`}
              >
                GR
              </button>
            </div>
            <Link
              to="/"
              data-testid="privacy-back"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-accent-dark hover:text-gold transition-colors"
            >
              <ArrowLeft size={14} /> Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {/* Table of contents */}
        <nav className="mb-16 border border-border-soft bg-white p-6">
          <p className="overline mb-3">Contents</p>
          <TocLink href="#privacy" label="Privacy Policy & GDPR Compliance" />
          <TocLink href="#cookies" label="Cookie Policy" />
          <TocLink href="#ip" label="Intellectual Property Policy" />
        </nav>

        {/* === PRIVACY POLICY === */}
        <div id="privacy">
          <DocTitle overline="Legal · Document 1 of 3" title="Privacy Policy" italic="& GDPR Compliance" />
          <InfoCard />

          <Section n="1" title="Introduction">
            <p>
              At Click Photography, we respect your privacy and are committed to
              protecting your personal data in accordance with the General Data
              Protection Regulation (GDPR) (EU) 2016/679.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, store, and protect
              your personal information when you visit our website, book a session,
              contact us, or use our services.
            </p>
            <p>By using this website, you agree to the terms of this Privacy Policy.</p>
          </Section>

          <Section n="2" title="Data Controller">
            <p>The data controller responsible for your personal data is:</p>
            <div className="bg-bg-alt p-5 mt-2">
              <p className="font-medium text-accent-dark">Click Photography</p>
              <p>Website: clickphotography.gr</p>
              <p>
                Email:{" "}
                <a href={`mailto:${SITE_EMAIL}`} className="text-gold hover:underline">
                  {SITE_EMAIL}
                </a>
              </p>
              <p>Location: Greece</p>
            </div>
          </Section>

          <Section n="3" title="What Personal Data We Collect">
            <p>We may collect and process the following information:</p>
            <p className="font-medium text-accent-dark mt-4">Information you provide directly</p>
            <List
              items={[
                "Full name",
                "Email address",
                "Phone number",
                "Booking details",
                "Messages sent through contact forms",
                "Information shared during photography bookings",
              ]}
            />
            <p className="font-medium text-accent-dark mt-6">Automatically collected information</p>
            <p>When you visit our website, we may automatically collect:</p>
            <List
              items={[
                "IP address",
                "Browser type",
                "Device information",
                "Pages visited",
                "Time spent on the website",
                "Cookies and analytics data",
              ]}
            />
          </Section>

          <Section n="4" title="How We Use Your Data">
            <p>We use your personal data for the following purposes:</p>
            <List
              items={[
                "To respond to inquiries",
                "To manage photography bookings",
                "To provide customer support",
                "To send booking confirmations and updates",
                "To improve website functionality and user experience",
                "To comply with legal obligations",
                "To protect against fraud or misuse",
              ]}
            />
            <p>We process your data only when we have a lawful basis to do so under GDPR.</p>
          </Section>

          <Section n="5" title="Legal Basis for Processing">
            <p>We process personal data based on:</p>
            <List
              items={[
                "Your consent",
                "Performance of a contract",
                "Compliance with legal obligations",
                "Legitimate business interests",
              ]}
            />
            <p>You may withdraw consent at any time.</p>
          </Section>

          <Section n="6" title="Photography & Image Usage">
            <p>
              As a photography business, we may capture and store photographs
              during sessions.
            </p>
            <p className="font-medium text-accent-dark">
              Client photographs will never be sold to third parties.
            </p>
            <p>We may use selected images for:</p>
            <List
              items={[
                "Portfolio display",
                "Website gallery",
                "Social media promotion",
                "Marketing materials",
              ]}
            />
            <p>Only with the client's consent where required by law.</p>
            <p>Clients may request removal of published images by contacting us.</p>
          </Section>

          <Section n="7" title="Cookies">
            <p>
              Our website may use cookies to improve user experience and analyze
              website traffic. Cookies may include:
            </p>
            <List
              items={[
                "Essential website cookies",
                "Analytics cookies",
                "Performance cookies",
                "Third-party service cookies",
              ]}
            />
            <p>Users can disable cookies through their browser settings.</p>
            <p>
              A separate Cookie Policy or Cookie Consent Banner is recommended for
              full GDPR compliance.
            </p>
          </Section>

          <Section n="8" title="Third-Party Services">
            <p>We may use trusted third-party services such as:</p>
            <List
              items={[
                "Google Analytics",
                "Meta / Facebook services",
                "Instagram integrations",
                "Booking or contact form providers",
                "Hosting providers",
                "Payment providers",
              ]}
            />
            <p>
              These providers may process data according to their own privacy
              policies.
            </p>
          </Section>

          <Section n="9" title="Data Storage & Security">
            <p>
              We take appropriate technical and organizational measures to protect
              personal data against:
            </p>
            <List items={["Unauthorized access", "Loss", "Misuse", "Disclosure", "Alteration"]} />
            <p>
              However, no online transmission or storage system can be guaranteed
              as completely secure.
            </p>
          </Section>

          <Section n="10" title="Data Retention">
            <p>We retain personal data only as long as necessary for:</p>
            <List
              items={[
                "Providing services",
                "Legal obligations",
                "Business and accounting purposes",
                "Resolving disputes",
              ]}
            />
            <p>
              Photography files may be stored for archival and backup purposes
              unless deletion is requested.
            </p>
          </Section>

          <Section n="11" title="Your GDPR Rights">
            <p>Under GDPR, you have the right to:</p>
            <List
              items={[
                "Access your personal data",
                "Correct inaccurate data",
                "Request deletion of your data",
                "Restrict processing",
                "Object to processing",
                "Withdraw consent",
                "Request data portability",
                "Lodge a complaint with a supervisory authority",
              ]}
            />
            <p>
              To exercise any of these rights, contact us at{" "}
              <a href={`mailto:${SITE_EMAIL}`} className="text-gold hover:underline">
                {SITE_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section n="12" title="Children's Privacy">
            <p>
              Our services are not directed toward children without parental
              involvement.
            </p>
            <p>
              Where family photography includes minors, consent is obtained from
              parents or legal guardians.
            </p>
          </Section>

          <Section n="13" title="International Data Transfers">
            <p>
              Some third-party services may store or process data outside the
              European Economic Area (EEA).
            </p>
            <p>
              Where this occurs, appropriate safeguards are used in accordance with
              GDPR requirements.
            </p>
          </Section>

          <Section n="14" title="Changes to This Privacy Policy">
            <p>We may update this Privacy Policy periodically.</p>
            <p>
              Any changes will be published on this page with an updated effective
              date.
            </p>
          </Section>

          <Section n="15" title="Contact Information">
            <p>For privacy-related questions or requests, contact:</p>
            <ContactBlock />
          </Section>

          <div className="mt-16 pt-10 border-t border-border-soft">
            <p className="overline">Recommended Additional GDPR Elements</p>
            <h3 className="font-serif text-2xl md:text-3xl text-accent-dark mt-4 font-light">
              For stronger GDPR compliance
            </h3>
            <p className="mt-4 text-text-muted">Your website should also include:</p>
            <List
              items={[
                "Cookie consent banner",
                "Terms & Conditions page",
                "Consent checkbox on contact forms",
                "SSL certificate (HTTPS)",
                "Privacy checkbox before booking submissions",
                "Data Processing Agreements with providers",
              ]}
            />
            <blockquote className="mt-6 border-l-2 border-gold pl-5 italic text-text-muted">
              "I agree that Click Photography may store and process my submitted
              information for the purpose of responding to my inquiry."
            </blockquote>
          </div>
        </div>

        {/* === COOKIE POLICY === */}
        <div id="cookies">
          <DocTitle overline="Legal · Document 2 of 3" title="Cookie" italic="Policy" />
          <InfoCard />

          <Section n="1" title="What Are Cookies?">
            <p>
              Cookies are small text files stored on your device when you visit a
              website. They help websites function properly, improve user
              experience, and provide analytics information.
            </p>
          </Section>

          <Section n="2" title="How We Use Cookies">
            <p>Click Photography uses cookies to:</p>
            <List
              items={[
                "Ensure proper website functionality",
                "Improve website performance",
                "Analyze visitor behavior and traffic",
                "Remember user preferences",
                "Support embedded social media features",
              ]}
            />
          </Section>

          <Section n="3" title="Types of Cookies We Use">
            <p className="font-medium text-accent-dark">Essential cookies</p>
            <p>These cookies are necessary for the website to function properly.</p>

            <p className="font-medium text-accent-dark mt-6">Analytics cookies</p>
            <p>These cookies help us understand how visitors use our website.</p>
            <p>Example providers may include:</p>
            <List items={["Google Analytics"]} />

            <p className="font-medium text-accent-dark mt-6">Functional cookies</p>
            <p>These cookies remember preferences such as language selection.</p>

            <p className="font-medium text-accent-dark mt-6">Third-party cookies</p>
            <p>
              Third-party services such as Facebook or Instagram may place cookies
              through embedded content or integrations.
            </p>
          </Section>

          <Section n="4" title="Managing Cookies">
            <p>You can control or delete cookies through your browser settings.</p>
            <p>Most browsers allow you to:</p>
            <List
              items={[
                "Block cookies",
                "Delete stored cookies",
                "Receive notifications before cookies are stored",
              ]}
            />
            <p>Disabling some cookies may affect website functionality.</p>
          </Section>

          <Section n="5" title="Consent">
            <p>
              When required by law, visitors will be asked to provide consent
              before non-essential cookies are activated.
            </p>
            <p>
              By continuing to use this website, you agree to the use of cookies
              as described in this policy.
            </p>
          </Section>

          <Section n="6" title="Contact">
            <p>For questions regarding this Cookie Policy, contact:</p>
            <ContactBlock />
          </Section>
        </div>

        {/* === INTELLECTUAL PROPERTY POLICY === */}
        <div id="ip">
          <DocTitle overline="Legal · Document 3 of 3" title="Intellectual" italic="Property Policy" />
          <InfoCard />

          <Section n="1" title="Ownership of Content">
            <p>
              All content displayed on this website is the intellectual property
              of Click Photography unless otherwise stated.
            </p>
            <p>This includes:</p>
            <List
              items={[
                "Photographs",
                "Logos",
                "Branding elements",
                "Website design",
                "Text content",
                "Graphics",
                "Videos",
                "Digital materials",
              ]}
            />
            <p className="font-medium text-accent-dark">All rights are reserved.</p>
          </Section>

          <Section n="2" title="Copyright Protection">
            <p>
              All photographs and creative materials are protected under copyright
              and intellectual property laws.
            </p>
            <p>
              Unauthorized use, reproduction, distribution, modification, or
              publication of any content from this website is strictly prohibited
              without prior written permission.
            </p>
          </Section>

          <Section n="3" title="Permitted Use">
            <p className="font-medium text-accent-dark">Visitors may:</p>
            <List
              items={[
                "View website content for personal use",
                "Share website links through social media",
              ]}
            />
            <p className="font-medium text-accent-dark mt-6">Visitors may not:</p>
            <List
              items={[
                "Download or reproduce images for commercial use",
                "Remove watermarks or branding",
                "Edit or manipulate photography work",
                "Use content for advertising or resale",
                "Copy website text or branding elements",
              ]}
            />
          </Section>

          <Section n="4" title="Client Image Usage">
            <p>
              Clients receive usage rights only as agreed in their photography
              contract or package.
            </p>
            <p>
              Copyright ownership of all images remains with Click Photography
              unless explicitly transferred in writing.
            </p>
          </Section>

          <Section n="5" title="Trademark & Branding">
            <p>
              The Click Photography name, logo, and branding elements may not be
              copied, imitated, or used without written permission.
            </p>
          </Section>

          <Section n="6" title="Reporting Copyright Violations">
            <p>
              If you believe your intellectual property rights have been violated
              on this website, please contact:
            </p>
            <ContactBlock />
            <p className="mt-4 font-medium text-accent-dark">Include:</p>
            <List
              items={[
                "Your contact information",
                "Description of the copyrighted work",
                "URL or location of the alleged infringement",
                "Proof of ownership",
              ]}
            />
          </Section>

          <Section n="7" title="Changes to This Policy">
            <p>
              We reserve the right to update this Intellectual Property Policy at
              any time.
            </p>
            <p>
              Changes will be published on this page with an updated effective
              date.
            </p>
          </Section>
        </div>

        <div className="mt-20 text-center">
          <Link
            to="/"
            data-testid="privacy-back-bottom"
            className="btn-luxury btn-luxury-dark"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </main>

      <footer className="bg-accent-dark text-white py-10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/55">
          <p>© {new Date().getFullYear()} Click Photography</p>
          <p>
            <a
              href={`mailto:${SITE_EMAIL}`}
              className="hover:text-gold transition-colors"
            >
              {SITE_EMAIL}
            </a>{" "}
            · {CONTACT.instagramHandle}
          </p>
        </div>
      </footer>
    </div>
  );
}
